
import React, { useState, useMemo } from 'react';
import { Scissors, CalendarClock, BookOpen, Search, Loader2, ChevronRight, Activity, User, HeartPulse, Monitor, ArrowUpRight, Award, Sparkles, ClipboardList } from 'lucide-react';
import { generateSurgicalReference } from '../services/geminiService';
import { User as UserType, CalendarEvent } from '../types';

interface DashboardProps {
    navigate: (view: string) => void;
    currentUser: UserType;
    events: CalendarEvent[];
}

// Specialized component for the On-Call Banner cards
const OnCallCard = ({ role, name, icon: Icon, color }: { role: string, name?: string, icon: any, color: string }) => (
    <div className="relative overflow-hidden bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-center gap-4 min-w-[200px] flex-1 transition-all hover:shadow-lg group shadow-md">
        <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
            <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold mb-0.5">{role}</div>
            <div className="text-slate-800 dark:text-white font-serif font-bold text-lg leading-none truncate">{name || 'Unassigned'}</div>
        </div>
    </div>
);

export const Dashboard: React.FC<DashboardProps> = ({ currentUser, events }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [referenceData, setReferenceData] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const now = useMemo(() => new Date(), []);
  const formattedDate = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(now);

  const greeting = useMemo(() => {
    const hour = now.getHours();
    if (hour >= 6 && hour < 12) return "Good Morning";
    if (hour >= 12 && hour < 18) return "Good Afternoon"; 
    return "Good Evening"; 
  }, [now]);

  const todaysTeam = useMemo(() => {
    const shiftDate = new Date(now);
    if (now.getHours() < 6) {
        shiftDate.setDate(shiftDate.getDate() - 1);
    }
    const dateKey = shiftDate.toLocaleDateString('en-CA');
    const event = events.find(e => e.date === dateKey && e.category === 'on-call');
    return event?.onCallTeam || { attending: 'None', resident: 'None', anesthesia: 'None' };
  }, [now, events]);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    setReferenceData(null);
    const result = await generateSurgicalReference(searchTerm);
    setReferenceData(result);
    setLoading(false);
  };

  // Uniform Portrait Card for Primary Portals
  const PortalPhotoCard = ({ title, sub, icon: Icon, href, image, accentColor }: {
    title: string;
    sub: string;
    icon: any;
    href: string;
    image: string;
    accentColor: string;
  }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col h-full rounded-2xl overflow-hidden bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      {/* Uniform 5:7 Portrait Frame Matching All 3 Photos */}
      <div className="relative w-full aspect-[5/7] bg-slate-950 overflow-hidden">
        <img
          src={image}
          alt={title}
          referrerPolicy="no-referrer"
          className="w-full h-full block object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        />
        <div className="absolute top-3 right-3 bg-slate-900/75 backdrop-blur-md p-2 rounded-full border border-white/15 opacity-80 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-4 h-4 text-white" />
        </div>
      </div>

      {/* Clean Uncovered Card Footer */}
      <div className="p-4 flex items-center gap-3.5 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700/80 mt-auto">
        <div className={`w-10 h-10 rounded-xl ${accentColor} flex items-center justify-center shrink-0 shadow-sm`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-slate-900 dark:text-white font-serif tracking-tight group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors truncate">
            {title}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 text-xs font-medium truncate">
            {sub}
          </p>
        </div>
      </div>
    </a>
  );

  return (
    <div className="p-6 md:p-8 h-full overflow-y-auto custom-scrollbar bg-slate-100 dark:bg-[#0f172a] text-slate-900 dark:text-slate-100 transition-colors">
        
        {/* Header Section */}
        <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 tracking-tight brand-font">
                    {greeting}, <span className="text-indigo-600 dark:text-indigo-400">
                        {currentUser.role === 'Resident' || currentUser.role === 'Attending' ? `Dr. ${currentUser.lastName}` : `${currentUser.firstName} ${currentUser.lastName}`}
                    </span>
                </h1>
                <p className="text-slate-500 dark:text-slate-400 font-medium text-sm flex items-center gap-2">
                    <CalendarClock className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <span>Mission-OP Surgical Suite</span>
                    <span aria-hidden="true">·</span>
                    <span>{formattedDate}</span>
                </p>
            </div>
        </header>

        {/* ON CALL BANNER */}
        <div className="relative w-full rounded-2xl overflow-hidden mb-8 shadow-lg border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-indigo-900 via-slate-900 to-slate-900">
            <div className="relative z-10 p-6 flex flex-col xl:flex-row items-start xl:items-center justify-between gap-6">
                <div className="flex items-center gap-4 min-w-[200px]">
                    <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/15">
                        <HeartPulse className="w-6 h-6 text-rose-300" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white brand-font">On Call Team</h2>
                        <p className="text-indigo-200 text-xs font-medium">Current active roster</p>
                    </div>
                </div>

                {/* Team Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full xl:w-auto flex-1">
                    <OnCallCard 
                        role="Attending Surgeon" 
                        name={todaysTeam.attending} 
                        icon={User} 
                        color="bg-indigo-600" 
                    />
                    <OnCallCard 
                        role="Surgical Resident" 
                        name={todaysTeam.resident} 
                        icon={Scissors} 
                        color="bg-sky-600" 
                    />
                    <OnCallCard 
                        role="Anesthesia" 
                        name={todaysTeam.anesthesia} 
                        icon={Activity} 
                        color="bg-teal-600" 
                    />
                </div>
            </div>
        </div>

        {/* Full-Fledged Surgical Portal Gallery (3 Equal-Sized Portrait Cards) */}
        <section className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
                <PortalPhotoCard 
                    title="EzyVet" 
                    sub="Clinic software & patient records" 
                    icon={Monitor}
                    href="https://mves.use1.ezyvet.com/login.php"
                    image="/assets/aistudio/PXL_20260722_161527011.RAW-02.ORIGINAL_c18176ef.jpg"
                    accentColor="bg-orange-500"
                />

                <PortalPhotoCard 
                    title="ACVS Portal" 
                    sub="Certification & case log management" 
                    icon={Award}
                    href="https://online.acvs.org/acvsssa/f?p=STDSSA:101:::::P101_VC,P101_CONTEXT,CUST_CONTEXT,G_SUCCESS_URL:AGILUTIONS,,,censsareqauth%3Fp_url%3Dhttps%253A%252F%252Fcert.acvs.org%252Feweb%252FDynamicPage.aspx%253Fwebcode%253DAASSOResponse%2526p_aa_token%253D__AUTHTOKEN__%2526IntendedUrl%253Dhttps%253A%252F%252Fcert.acvs.org%252Feweb%252FDynamicpage.aspx%253Fwebcode%253DCertProfile%2526cr2_key%253D477A240A-BEB7-44DC-B2F8-7BE541AEB691%26p_context%3D%26p_vc%3DAGILUTIONS"
                    image="/assets/aistudio/PXL_20250227_162714692_65265e45.jpg"
                    accentColor="bg-emerald-600"
                />

                <PortalPhotoCard 
                    title="Smart Flow" 
                    sub="Treatment sheets & whiteboard" 
                    icon={ClipboardList}
                    href="https://www.smartflowsheet.com/Account/Login?ReturnUrl=%2Fwhiteboard"
                    image="/assets/aistudio/PXL_20261012_151609550_955c9dc7.jpg"
                    accentColor="bg-sky-500"
                />
            </div>
        </section>

        {/* Bottom Section: Tobias Surgical Reference (Full Width) */}
        <div className="pb-6">
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-md flex flex-col overflow-hidden min-h-[260px]">
                <div className="p-5 border-b border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2.5 rounded-xl">
                            <Sparkles className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white brand-font">Tobias Reference</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Quick Clinical Summaries</p>
                        </div>
                    </div>
                    <form onSubmit={handleSearch} className="relative group w-full sm:w-96">
                        <input 
                            type="text" 
                            placeholder="Search procedure (e.g. TPLO, GDV)..." 
                            className="w-full pl-10 pr-12 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500 transition-all font-sans text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Search className="w-4 h-4 text-slate-400 dark:text-slate-500 absolute left-3.5 top-3 group-focus-within:text-indigo-500 transition-colors" />
                        <button 
                            type="submit"
                            disabled={loading}
                            className="absolute right-1.5 top-1.5 bottom-1.5 bg-indigo-600 hover:bg-indigo-500 text-white px-3 rounded-lg disabled:opacity-50 transition-all flex items-center justify-center"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                    </form>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 custom-scrollbar bg-slate-50/40 dark:bg-slate-900/20">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-8 text-slate-400 space-y-3">
                            <Loader2 className="w-8 h-8 animate-spin text-indigo-500 dark:text-indigo-400" />
                            <p className="font-medium text-xs text-indigo-600/70 dark:text-indigo-300/70">Generating clinical summary...</p>
                        </div>
                    ) : referenceData ? (
                        <div className="prose prose-sm prose-slate dark:prose-invert max-w-none">
                            <div className="markdown-content whitespace-pre-wrap leading-relaxed text-slate-700 dark:text-slate-300">
                                {referenceData}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-8 text-center space-y-3 opacity-70">
                            <BookOpen className="w-8 h-8 text-slate-400 dark:text-slate-500" />
                            <p className="text-sm font-medium text-slate-500 dark:text-slate-400 max-w-xs">
                                Enter any surgical procedure above for a concise clinical reference summary.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
  );
};
